import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useClickAway } from 'react-use'
import classNames from 'classnames'
import sample from 'lodash/sample'

import { Body, TextInput } from '@foxino/components-web'
import { SearchLg } from '@foxino/icons-react'

import { Language, type LearningFeatureShortDetailFragment, type LearningFeatureType } from '@app/data'
import { useTestId } from '@app/hooks/useTestId'
import { useTranslation } from '@app/locales'
import { useSearchLearningFeature } from '@app/modules/common/hooks/useSearchLearningFeature'
import { LoaderWrapper } from '@app/modules/common/view/LoaderWrapper'
import { getImageUrlFromImageType } from '@app/utils/getImageUrlFromImageType'
import { LearningFeatureUtils } from '@app/utils/LearningFeatureUtils/LearningFeatureUtils'

type Props = {
  isReverse?: boolean
  learningFeatureType?: LearningFeatureType[]
  side?: 'bottom' | 'top'
  className?: string
  autoFocus?: boolean
  disabledFeatures?: string[]
  targetLanguage?: Language
  sourceLanguage?: Language
  disabled?: boolean
  onValueSelect?: (learningFeature: LearningFeatureShortDetailFragment) => void
}

const MAX_SEARCH_SIZE = 15

export const ContentSuggestionMenu = forwardRef(
  (
    {
      isReverse,
      learningFeatureType,
      side,
      disabled,
      className,
      disabledFeatures = [],
      sourceLanguage = Language.CZECH,
      targetLanguage = Language.ENGLISH,
      autoFocus = true,
      onValueSelect
    }: Props,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const testId = useTestId(['learning_plan'])
    const { t } = useTranslation('learningPlan')

    const wrapperDivRef = useRef<HTMLDivElement | null>(null)
    const inputRef = useRef<HTMLInputElement | null>(null)

    useImperativeHandle(ref, () => wrapperDivRef.current!, [])

    useClickAway(wrapperDivRef, () => {
      setSearchValue('')
    })

    const [searchValue, setSearchValue] = useState('')
    const [selectedIndex, setSelectedIndex] = useState(-1)
    const scrollRef = useRef<HTMLDivElement>(null)
    const selectedItemRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({})

    const { actions, learningFeatures, loading, error } = useSearchLearningFeature()

    if (isReverse) {
      learningFeatures.reverse()
    }

    useEffect(() => {
      if (learningFeatures.length > 0) {
        setSelectedIndex(0)
      }
    }, [learningFeatures])

    useEffect(() => {
      if (searchValue.length >= 3) {
        actions.searchLearningFeature({
          text: searchValue,
          size: MAX_SEARCH_SIZE,
          learningFeatureType,
          sourceLanguage,
          targetLanguage
        })
      }
      setSelectedIndex(-1)
      selectedItemRefs.current = {}
    }, [searchValue])

    useEffect(() => {
      const container = scrollRef.current
      if (container && isReverse) {
        container.scrollTop = container.scrollHeight
      }
    }, [learningFeatures && isReverse])

    useEffect(() => {
      if (selectedIndex >= 0) {
        if (selectedItemRefs.current[selectedIndex]) {
          selectedItemRefs.current[selectedIndex]?.scrollIntoView({
            behavior: 'smooth'
          })
        } else {
          const container = scrollRef.current

          if (container) {
            const itemHeight = 44
            const targetScrollTop = selectedIndex * itemHeight
            container.scrollTo({
              top: targetScrollTop,
              behavior: 'smooth'
            })
          }
        }
      }
    }, [selectedIndex])

    const handleOnValueSelect = (learningFeature: LearningFeatureShortDetailFragment) => {
      const isDisabled = disabledFeatures.some((dlf) => dlf === learningFeature.id)
      if (isDisabled) return

      setSearchValue('')
      onValueSelect?.(learningFeature)

      inputRef.current?.focus()
    }

    const handleKeyNavigation = (e: React.KeyboardEvent) => {
      if (!learningFeatures.length || searchValue.length < 3) return

      if (!learningFeatures.length) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prevIndex) => {
            const newIndex = prevIndex < learningFeatures.length - 1 ? prevIndex + 1 : 0
            return newIndex
          })
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prevIndex) => {
            const newIndex = prevIndex > 0 ? prevIndex - 1 : learningFeatures.length - 1
            return newIndex
          })
          break
        case 'Enter':
          e.preventDefault()
          if (selectedIndex >= 0 && selectedIndex < learningFeatures.length) {
            const newFeature = learningFeatures[selectedIndex] as LearningFeatureShortDetailFragment
            const isDisabled = disabledFeatures.some((dlf) => dlf === newFeature.id)

            if (isDisabled) return
            handleOnValueSelect(newFeature)
          }
          break
        case 'Escape':
          e.preventDefault()
          setSearchValue('')
          setSelectedIndex(-1)
          break
      }
    }

    return (
      <div ref={wrapperDivRef} className={classNames('relative', className)}>
        <TextInput
          disabled={disabled}
          ref={inputRef}
          autoFocus={autoFocus}
          value={searchValue}
          data-testid={testId('lesson_page.content_library.search_field')}
          className={classNames('mt-2')}
          inputClassName="bg-primary-1000"
          wrapperClassName="bg-primary-1000"
          placeholder={t('contentLibrary.searchLFPlaceholder')}
          LeadingIcon={SearchLg}
          onChange={(e) => setSearchValue(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyNavigation}
        />

        {searchValue.length >= 3 && (
          <div
            className={classNames(
              'absolute left-0 z-[60] flex h-auto max-h-[196px] w-full flex-col gap-1 rounded-lg border-2 border-solid border-primary-600 bg-primary-1000 p-1 shadow-lg',
              side === 'top' ? 'bottom-[42px]' : 'top-12'
            )}
          >
            <LoaderWrapper
              loading={loading}
              error={error}
              size="xs"
              className="flex h-full min-h-11 w-full items-center justify-center"
            >
              {learningFeatures.length > 0 ? (
                <div ref={scrollRef} className="flex h-auto min-h-11 w-full flex-col gap-2 overflow-y-auto">
                  {(() => {
                    let enabledIndex = -1

                    return learningFeatures.map((lf) => {
                      const isDisabled = disabledFeatures.some((dlf) => dlf === lf.id)
                      enabledIndex++
                      const isSelected = enabledIndex === selectedIndex

                      return (
                        <SuggestionMenuItem
                          key={lf.id}
                          learningFeature={lf}
                          disabled={isDisabled}
                          isSelected={isSelected}
                          onValueSelect={handleOnValueSelect}
                          ref={(el) => (selectedItemRefs.current[enabledIndex] = el)}
                        />
                      )
                    })
                  })()}
                </div>
              ) : (
                <div className="flex min-h-11 w-full items-center justify-center text-sm text-neutral-400 opacity-60">
                  {t('contentLibrary.noResultsFound')}
                </div>
              )}
            </LoaderWrapper>
          </div>
        )}
      </div>
    )
  }
)

ContentSuggestionMenu.displayName = 'ContentSuggestionMenu'

const SuggestionMenuItem = forwardRef<
  HTMLButtonElement,
  {
    learningFeature: LearningFeatureShortDetailFragment
    disabled: boolean
    isSelected?: boolean
    onValueSelect: (learningFeature: LearningFeatureShortDetailFragment) => void
  }
>(({ learningFeature, disabled, isSelected = false, onValueSelect }, ref) => {
  const translationFeature = LearningFeatureUtils.getTranslationLearningFeature(learningFeature)
  const grammarFeature = LearningFeatureUtils.getGrammarLearningFeature(learningFeature)

  const testId = useTestId(['learning_plan'])
  const image = useMemo(
    () => getImageUrlFromImageType(sample(translationFeature?.images), 'medium'),
    [translationFeature]
  )

  return (
    <button
      ref={ref}
      disabled={disabled}
      data-testid={testId('lesson_page.content_library.search_result')}
      className={classNames(
        'group flex min-h-11 w-full flex-row items-center justify-center rounded-lg px-2 py-1 outline-none focus-visible:outline-1 focus-visible:outline-white focus-visible:ring-0',
        isSelected
          ? 'cursor-pointer border-2 border-primary-600 bg-primary-800'
          : 'cursor-pointer border border-primary-800 bg-primary-900 hover:bg-primary-700',
        { 'cursor-not-allowed opacity-50': disabled }
      )}
      onClick={(e) => {
        e.stopPropagation()
        onValueSelect(learningFeature)
      }}
    >
      {translationFeature ? (
        <div className="flex w-full flex-row items-center gap-1">
          <div className="line-clamp-2 text-base font-medium text-primary-100">
            {translationFeature.targetLanguageText}
          </div>
          <div className="font-medium text-primary-300 opacity-40">/</div>
          <div className="line-clamp-2 text-base text-primary-300">{translationFeature.sourceLanguageText}</div>
        </div>
      ) : grammarFeature ? (
        <div className="flex w-full flex-col items-start gap-0.5">
          <div className="flex w-full flex-row items-center gap-1">
            <Body className="line-clamp-2 text-primary-100">{grammarFeature.name}</Body>
            <Body className="text-primary-300 opacity-40">/</Body>
            <Body className="line-clamp-2 text-primary-300">{grammarFeature.subtitle}</Body>
          </div>
          <Body size="xs" className="line-clamp-2 text-primary-400">
            {`(${grammarFeature.example?.fullText})`}
          </Body>
        </div>
      ) : null}

      {image && <img src={image} className="h-9 w-12" />}
    </button>
  )
})

SuggestionMenuItem.displayName = 'SuggestionMenuItem'
